import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../contexts";
import LanguageSwitcher from "../components/LanguageSwitcher";

const LoginPage = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/");
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const userData = await login(email, password);
      toast.success(t('auth.loginSuccess'));
      // Installers go directly to calendar
      if (userData?.role === 'installer') {
        navigate("/calendar");
      } else {
        navigate("/");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.loginError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="absolute top-4 right-4">
        <div className="bg-white rounded-lg shadow-lg">
          <LanguageSwitcher />
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-1 mb-4">
            <span className="text-4xl font-bold text-gray-800">Lo</span>
            <span className="text-4xl font-bold text-lojack-red">/</span>
            <span className="text-4xl font-bold text-gray-800">Jack</span>
          </div>
          <h2 className="text-xl text-gray-600">{t('auth.installerCalendar')}</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.email')}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lojack-red focus:border-transparent transition"
              placeholder="email@lojack.it"
              required
              data-testid="login-email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.password')}</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lojack-red focus:border-transparent transition pr-12"
                placeholder="••••••••"
                required
                data-testid="login-password"
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                data-testid="toggle-password">
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-lojack-red text-white py-3 rounded-lg font-semibold hover:bg-red-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="login-submit"
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              t('auth.login')
            )}
          </button>

          <div className="text-center mt-4 space-y-1">
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              className="text-sm text-gray-500 hover:text-lojack-red transition"
              data-testid="forgot-password-link"
            >
              {t('auth.forgotPassword')}
            </button>
            <div>
              <a
                href="/reset.html"
                className="text-xs text-gray-400 hover:text-gray-600 transition underline decoration-dotted underline-offset-2"
                data-testid="reset-access-link"
              >
                Problemi di accesso? Ripristina qui
              </a>
            </div>
          </div>
        </form>

        <div className="mt-6 pt-4 border-t border-gray-200 text-center">
          <a
            href="https://telcavoip.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition"
          >
            <span>{t('footer.poweredBy')}</span>
            <span className="font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">TelcaVoIP AI</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
